/**
 * Copy Diagnostics to Clipboard
 * Safely copies diagnostics snapshot to clipboard with redaction gate
 */

/* global document, navigator */

import { diagnosticsStore } from "./diagnosticsStore";

const MAX_JSON_SIZE = 20 * 1024; // 20 KB

// Denylist keywords (case-insensitive)
const DENYLIST_KEYWORDS = [
  "authorization",
  "bearer",
  "token",
  "apikey",
  "cookie",
  "set-cookie",
  "email",
  "@",
  "workbook",
  "worksheet",
  "address",
  "formula",
];

/**
 * Strip control characters (except newline and tab) from a string
 */
function stripControlChars(str: string): string {
  // Remove control characters except newline (\n = 10) and tab (\t = 9)
  return str.replace(/[\x00-\x08\x0B-\x1F\x7F]/g, "");
}

/**
 * Check if JSON string contains any denylist keywords
 */
function containsDenylistKeywords(jsonStr: string): boolean {
  const lowerStr = jsonStr.toLowerCase();
  for (const keyword of DENYLIST_KEYWORDS) {
    if (lowerStr.includes(keyword.toLowerCase())) {
      return true;
    }
  }
  return false;
}

/**
 * Copy diagnostics snapshot to clipboard
 * Returns { ok: boolean, error?: string }
 */
export async function copyDiagnosticsToClipboard(): Promise<{
  ok: boolean;
  error?: string;
}> {
  try {
    // Get snapshot
    const snapshot = diagnosticsStore.getSnapshot();

    // Stringify with 2-space indent
    let jsonStr = JSON.stringify(snapshot, null, 2);

    // Length cap: Block if too large
    if (jsonStr.length > MAX_JSON_SIZE) {
      return {
        ok: false,
        error: "Diagnostics too large; copy blocked.",
      };
    }

    // Strip control characters (except newline/tab)
    jsonStr = stripControlChars(jsonStr);

    // Redaction gate: Check for denylist keywords
    if (containsDenylistKeywords(jsonStr)) {
      return {
        ok: false,
        error: "Diagnostics contains potentially sensitive fields; copy blocked.",
      };
    }

    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(jsonStr);
        return { ok: true };
      } catch (clipboardError) {
        // Fall through to fallback
      }
    }

    // Fallback: Use hidden textarea + execCommand
    const textarea = document.createElement("textarea");
    textarea.value = jsonStr;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();

    try {
      const success = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (success) {
        return { ok: true };
      } else {
        return {
          ok: false,
          error: "Copy command failed.",
        };
      }
    } catch (execError) {
      document.body.removeChild(textarea);
      return {
        ok: false,
        error: "Copy failed: " + (execError instanceof Error ? execError.message : "unknown"),
      };
    }
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}
